import { PageHeader } from '@/components/PageHeader';
import { Button } from '@/components/ui/button';
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChevronDown, ChevronRight, SearchX } from 'lucide-react';
import { Hint } from '@/components/Hint';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import type { OrderSummary, OverviewReceiving } from '@/api/orders';
import { OrderRow, orderGridClass } from '@/features/orders/OrderRow';
import { cn } from '@/lib/utils';
import { WorkEmpty } from '@/features/orders/workLib';
import {
  STAGE_ORDER,
  STAGE_TITLES,
  stageOf,
  toOrderCompare,
  type Stage,
} from '@/features/orders/lib';
import { useOrdersDataStore } from '@/stores/ordersDataStore';
import {
  ordersScrollScope,
  useOrdersViewStore,
  type OrdersGroupBy as GroupBy,
  type OrdersSortBy as SortBy,
  type OrdersStageFilter,
} from '@/stores/ordersViewStore';
import {
  SCROLL_KEY_ATTR,
  useScrollRestoration,
} from '@/lib/scrollRestoration';
import { PageState } from './PageState';

/** Searchable order browsing. Detail work lives on its own route; filters and
 * list scroll position survive navigation through the shared view store. */
export function JourneysPage() {
  const [advanced, setAdvanced] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const orders = useOrdersDataStore((s) => s.orders);
  const overview = useOrdersDataStore((s) => s.overview);
  const loading = useOrdersDataStore((s) => s.loading);
  const error = useOrdersDataStore((s) => s.error);
  const includeArchived = useOrdersDataStore((s) => s.includeArchived);
  const setIncludeArchived = useOrdersDataStore((s) => s.setIncludeArchived);
  const load = useOrdersDataStore((s) => s.load);

  const search = useOrdersViewStore((s) => s.search);
  const setSearch = useOrdersViewStore((s) => s.setSearch);
  const sortBy = useOrdersViewStore((s) => s.sortBy);
  const setSortBy = useOrdersViewStore((s) => s.setSortBy);
  const groupBy = useOrdersViewStore((s) => s.groupBy);
  const setGroupBy = useOrdersViewStore((s) => s.setGroupBy);
  const collapsed = useOrdersViewStore((s) => s.collapsed);
  const setCollapsed = useOrdersViewStore((s) => s.setCollapsed);

  const stageFilter = (searchParams.get('stage') || 'all') as OrdersStageFilter;

  const receivingsByOrder = useMemo(() => {
    const map = new Map<string, OverviewReceiving[]>();
    for (const rcv of overview?.receivings ?? []) {
      const list = map.get(rcv.orderId) || [];
      list.push(rcv);
      map.set(rcv.orderId, list);
    }
    return map;
  }, [overview]);

  // Open decisions per journey: part of a row's next action, so a journey with
  // three items waiting in To decide never reads as calm on the list.
  const actionsByOrder = useMemo(() => {
    const map = new Map<string, number>();
    for (const a of overview?.actions ?? []) {
      if (a.orderId) map.set(a.orderId, (map.get(a.orderId) ?? 0) + 1);
    }
    return map;
  }, [overview]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let out = orders;
    if (q) {
      out = out.filter(
        (o) =>
          o.vendor.toLowerCase().includes(q) ||
          (o.label || '').toLowerCase().includes(q) ||
          o.receivingIds.some((id) => String(id).includes(q))
      );
    }
    if (stageFilter !== 'all') {
      out = out.filter((o) => stageOf(o) === stageFilter);
    }
    return out;
  }, [orders, search, stageFilter]);

  const sorted = useMemo(() => {
    const stageRank = (o: OrderSummary) => STAGE_ORDER.indexOf(stageOf(o));
    const byDate = (a: OrderSummary, b: OrderSummary) =>
      (b.placedDate || '').localeCompare(a.placedDate || '') || Number(b.id) - Number(a.id);
    const out = [...filtered];
    if (sortBy === 'vendor') out.sort((a, b) => a.vendor.localeCompare(b.vendor) || byDate(a, b));
    else if (sortBy === 'status') out.sort((a, b) => stageRank(a) - stageRank(b) || byDate(a, b));
    else out.sort(byDate);
    return out;
  }, [filtered, sortBy]);

  const groups = useMemo(() => {
    if (groupBy === 'none') return [{ key: 'all', title: null as string | null, orders: sorted }];
    if (groupBy === 'vendor') {
      const map = new Map<string, OrderSummary[]>();
      for (const order of sorted) {
        const list = map.get(order.vendor) || [];
        list.push(order);
        map.set(order.vendor, list);
      }
      return [...map.entries()]
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([vendor, list]) => ({ key: vendor, title: vendor, orders: list }));
    }
    // By stage, in journey order — the "what's going on right now" read.
    const byStage = new Map<Stage, OrderSummary[]>();
    for (const order of sorted) {
      const stage = stageOf(order);
      const list = byStage.get(stage) || [];
      list.push(order);
      byStage.set(stage, list);
    }
    // To-order journeys sort by their deadline — the window closing soonest is
    // the row that matters, and they have no placed_date to sort on anyway.
    byStage.get('to_order')?.sort(toOrderCompare);
    return STAGE_ORDER.filter((s) => byStage.has(s)).map((s) => ({
      key: s as string,
      title: STAGE_TITLES[s],
      orders: byStage.get(s)!,
    }));
  }, [sorted, groupBy]);

  // One scroll container per view; the restoration scope tells filter
  // combinations apart, so each list keeps its own position and a fresh
  // combination starts at the top.
  const scrollViewportRef = useRef<HTMLDivElement | null>(null);
  const ready = Boolean(overview) || (!loading && !error);
  useScrollRestoration({
    scopeKey: ordersScrollScope({
      view: 'orders',
      stageFilter,
      includeArchived,
      sortBy,
      groupBy,
      search,
    }),
    viewportRef: scrollViewportRef,
    ready,
  });

  // Quick nav: jump the list to a stage group. The column header is sticky
  // inside the viewport, so a group scrolled flush to the top would hide under
  // it — offset by its height (0 when the header is hidden on narrow layouts).
  const groupRefs = useRef(new Map<string, HTMLDivElement | null>());
  const columnHeaderRef = useRef<HTMLDivElement | null>(null);
  const [pendingJump, setPendingJump] = useState<string | null>(null);

  const scrollToGroup = useCallback((key: string) => {
    const viewport = scrollViewportRef.current;
    const el = groupRefs.current.get(key);
    if (!viewport || !el) return;
    const offset = columnHeaderRef.current?.offsetHeight ?? 0;
    const top =
      viewport.scrollTop +
      el.getBoundingClientRect().top -
      viewport.getBoundingClientRect().top -
      offset;
    viewport.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  }, []);

  // A collapsed target has to open first, so that jump waits for the re-render.
  useLayoutEffect(() => {
    if (!pendingJump) return;
    scrollToGroup(pendingJump);
    setPendingJump(null);
  }, [pendingJump, scrollToGroup]);

  const jumpToGroup = (key: string) => {
    if (collapsed[key]) {
      setCollapsed((c) => ({ ...c, [key]: false }));
      setPendingJump(key);
    } else {
      scrollToGroup(key);
    }
  };

  const setStage = (stage: string) => {
    const next = new URLSearchParams(searchParams);
    if (stage === 'all') next.delete('stage');
    else next.set('stage', stage);
    setSearchParams(next);
  };

  const journeyFilters = (
    <>
      <Input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        aria-label="Search journeys by vendor, release, or receiving" placeholder="Search vendor, release, receiving…"
        className="h-9 w-72 text-sm"
      />
      <Select value={stageFilter} onValueChange={setStage}>
        <SelectTrigger aria-label="Stage filter" className="h-9 w-36 text-sm">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All stages</SelectItem>
          {STAGE_ORDER.map((stage) => (
            <SelectItem key={stage} value={stage}>
              {STAGE_TITLES[stage]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Button variant="ghost" className="md:hidden" aria-expanded={advanced} aria-controls="journey-secondary-filters" onClick={() => setAdvanced(v => !v)}>{advanced ? 'Fewer filters' : 'More filters'}</Button>
      <div id="journey-secondary-filters" className="contents max-md:hidden max-md:data-[expanded=true]:flex max-md:data-[expanded=true]:w-full max-md:data-[expanded=true]:flex-wrap max-md:data-[expanded=true]:gap-2.5" data-expanded={advanced}>
      <Select value={sortBy} onValueChange={(v) => setSortBy(v as SortBy)}>
        <SelectTrigger aria-label="Sort journeys" className="h-9 w-36 text-sm">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="date">Newest first</SelectItem>
          <SelectItem value="vendor">Vendor A–Z</SelectItem>
          <SelectItem value="status">By stage</SelectItem>
        </SelectContent>
      </Select>
      <Select value={groupBy} onValueChange={(v) => setGroupBy(v as GroupBy)}>
        <SelectTrigger aria-label="Group journeys" className="h-9 w-40 text-sm">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="stage">Group by stage</SelectItem>
          <SelectItem value="vendor">Group by vendor</SelectItem>
          <SelectItem value="none">No grouping</SelectItem>
        </SelectContent>
      </Select>
      <Hint content="Include archived journeys">
        <label className="flex cursor-pointer items-center gap-1.5 text-xs text-muted-foreground">
          <Switch checked={includeArchived} onCheckedChange={setIncludeArchived} />
          Archived
        </label>
      </Hint>
      </div>
      <span className="ml-auto hidden shrink-0 text-xs text-muted-foreground md:flex">
        {filtered.length} of {orders.length} journeys
      </span>
    </>
  );

  return (
    <div className="relative flex h-full flex-col overflow-hidden bg-background">
      <PageHeader title="Journeys" />
      <div className="@container mx-8 mb-6 flex min-h-0 flex-1 flex-col overflow-hidden rounded-[10px] border border-border bg-card shadow-card max-md:mx-3 max-md:mb-4" data-group={groupBy}>
      <div className="flex shrink-0 flex-wrap items-center gap-2.5 border-b border-border bg-card px-5 py-3.5 max-md:p-3 [&_input]:min-w-0 max-md:[&>input]:w-full max-md:[&>input]:flex-[1_1_100%]">
        {journeyFilters}
      </div>

      {groupBy === 'stage' && groups.length > 1 && (
        <nav
          aria-label="Jump to stage"
          className="flex shrink-0 flex-wrap items-center gap-1.5 border-b border-border bg-card px-5 py-2 max-md:px-3"
        >
          {groups.map((group) => (
            <button
              key={group.key}
              type="button"
              className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-2.5 py-1 text-[12px] text-muted-foreground hover:bg-muted hover:text-foreground"
              onClick={() => jumpToGroup(group.key)}
            >
              {group.title}
              <span className="font-display font-bold tabular-nums opacity-60">{group.orders.length}</span>
            </button>
          ))}
        </nav>
      )}

      <div ref={scrollViewportRef} className="min-h-0 flex-1 overflow-y-auto">
        <PageState loading={loading && !overview} error={error} onRetry={() => void load()} />

        {ready && (
          <div>
            {filtered.length === 0 && !loading && (
              <WorkEmpty
                icon={search || stageFilter !== 'all' ? <SearchX /> : undefined}
                detail={(search || stageFilter !== 'all') && (
                  <Button variant="outline" onClick={() => { setSearch(''); setStage('all'); }}>Clear filters</Button>
                )}
              >
                {search || stageFilter !== 'all' ? 'No journeys match these filters.' : 'No journeys yet.'}
              </WorkEmpty>
            )}

            <div ref={columnHeaderRef} className={cn(orderGridClass(groupBy === 'stage'), 'sticky top-0 z-20 border-b border-border bg-muted py-2.5 pl-5 pr-[50px] text-[11px] font-bold uppercase tracking-[.06em] text-muted-foreground max-md:hidden @max-[650px]:hidden')}><span>Vendor / release</span><span className={cn(groupBy === 'stage' && 'hidden')}>Stage</span><span>Next action / blocker</span><span>Date</span><span className="max-[1200px]:hidden">Receiving</span></div>
            {groups.map((group) => {
              // Done/Cancelled default collapsed on the full list — but a stage
              // FILTER means "show me these": the lone group it produces must
              // never render as an empty collapsed header.
              const isCollapsed =
                stageFilter === 'all' && group.title != null && Boolean(collapsed[group.key]);
              return (
                <div
                  key={group.key}
                  ref={(el) => {
                    groupRefs.current.set(group.key, el);
                  }}
                >
                  {group.title != null && (
                    <button
                      type="button"
                      {...{ [SCROLL_KEY_ATTR]: `group:${group.key}` }}
                      className="flex w-full items-center gap-2 border-b border-border bg-card px-5 py-3 text-[12px] font-semibold text-muted-foreground"
                      onClick={() => setCollapsed((c) => ({ ...c, [group.key]: !c[group.key] }))}
                    >
                      {isCollapsed ? (
                        <ChevronRight className="h-3 w-3" />
                      ) : (
                        <ChevronDown className="h-3 w-3" />
                      )}
                      {group.title}
                      <span className="font-normal">({group.orders.length})</span>
                    </button>
                  )}
                  {!isCollapsed &&
                    group.orders.map((order) => (
                      <OrderRow
                        key={order.id}
                        order={order}
                        receivings={receivingsByOrder.get(order.id) || []}
                        openActions={actionsByOrder.get(order.id) ?? 0}
                        grouped={groupBy === 'stage'}
                      />
                    ))}
                </div>
              );
            })}
          </div>
        )}
      </div>
      </div>
    </div>
  );
}
